/**
 * Common formatting utilities for time and distance
 * Extracted to eliminate duplication across components
 */
import { createLogger } from './logger'

const logger = createLogger('Formatters')

/**
 * Format time ago from a date string
 * @param {string} dateString - ISO date string
 * @param {function} t - Translation function (optional)
 * @returns {string} Formatted time ago string
 */
export function formatTimeAgo(dateString, t = null) {
  if (!dateString) return t ? t('common.recently', 'Recently') : 'Recently'
  
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return t ? t('common.recently', 'Recently') : 'Recently'
  
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  const agoText = t ? t('dashboard.compact.ago', 'ago') : 'ago'

  if (diffMins < 1) return t ? t('common.justNow', 'Just now') : 'Just now'
  if (diffMins < 60) return `${diffMins}m ${agoText}`
  if (diffHours < 24) return `${diffHours}h ${agoText}`
  return `${diffDays}d ${agoText}`
}

/**
 * Format distance from meters to miles
 * @param {number} distanceInMeters - Distance in meters
 * @returns {string|null} Formatted distance string or null
 */
export function formatDistance(distanceInMeters) {
  if (!distanceInMeters && distanceInMeters !== 0) return null
  const distanceInMiles = distanceInMeters * 0.000621371
  if (distanceInMiles < 0.1) return '<0.1 mi'
  return `${distanceInMiles.toFixed(1)} mi`
}

/**
 * Format date to locale string
 * @param {string} dateString - ISO date string
 * @param {string} locale - Locale code (e.g., 'en', 'es-MX')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDate(dateString, locale = 'en', options = { month: 'short', year: 'numeric' }) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  
  // Normalize locale for Intl API (convert to lowercase with hyphen)
  // 'es-MX' -> 'es-mx', 'pt-BR' -> 'pt-br', 'fr-CA' -> 'fr-ca'
  const normalizedLocale = locale.toLowerCase().replace('_', '-')
  
  try {
    return date.toLocaleDateString(normalizedLocale, options)
  } catch (err) {
    // Fallback to English if locale is invalid
    logger.warn(`Invalid locale "${locale}", falling back to English`, err)
    return date.toLocaleDateString('en', options)
  }
}

/**
 * Get freshness color class based on time elapsed
 * @param {string} dateString - ISO date string
 * @returns {string} Tailwind color class
 */
export function getFreshnessColor(dateString) {
  if (!dateString) return 'bg-gray-500'
  
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'bg-gray-500'
  
  const now = new Date()
  const diffHours = Math.floor((now - date) / 3600000)
  
  if (diffHours < 6) return 'bg-green-500'  // Fresh (0-6h)
  if (diffHours < 24) return 'bg-blue-500'  // Recent (6-24h)
  if (diffHours < 48) return 'bg-yellow-500' // Stale (24-48h)
  return 'bg-gray-500'                       // Old (48h+)
}
